import {
  ProductOperation,
  UNAUTHENTICATED_SEARCH_PRODUCTS
} from "@/api/graphQl/unauthenticated/Product";
import { getAllUserRetailerShoppingCarts } from "@/api/rest/checkoutApi";
import { getEntityForLandingPage } from "@/api/rest/unauthenticated/Site";
import { ThunkExtraArguments } from "@/redux/store";
import { ApolloClient } from "@apollo/client";
import { createAsyncThunk } from "@reduxjs/toolkit";
import _sortBy from "lodash/sortBy";
import { AUTHENTICATED_SEARCH_PRODUCTS } from "../../../api/graphQl/authenticated/Product";
import ReduxStore from "../../types";
/**
 * Fetches product metadata based on the stackline SKU
 * @param apolloClient - Apollo client for GraphQL queries
 * @param id - Stackline SKU
 * @param isAuthenticated - Whether the user is authenticated
 * @returns Product entity data
 */
export const fetchProductMetadataWithGraphQL = async (
  apolloClient: ApolloClient<object>,
  stacklineSkus: string[],
  isAuthenticated: boolean
) => {
  let productEntities: any[] = [];
  if (isAuthenticated) {
    const productEntityRes = await apolloClient.query({
      query: AUTHENTICATED_SEARCH_PRODUCTS,
      variables: {
        page: { start: 0, size: stacklineSkus.length },
        stacklineSkus,
        computeFacets: false,
      },
    });

    if (
      productEntityRes?.data?.[ProductOperation.AuthenticatedSearch]?.products
        ?.length
    ) {
      productEntities =
        productEntityRes?.data?.[ProductOperation.AuthenticatedSearch]
          ?.products;
    }
  } else {
    const productEntityRes = await apolloClient.query({
      query: UNAUTHENTICATED_SEARCH_PRODUCTS,
      variables: {
        page: { start: 0, size: stacklineSkus.length },
        stacklineSkus,
        computeFacets: false,
      },
    });

    if (
      productEntityRes?.data?.[ProductOperation.UnauthenticatedSearch]?.products
        ?.length
    ) {
      productEntities =
        productEntityRes?.data?.[ProductOperation.UnauthenticatedSearch]
          ?.products;
    }
  }
  return productEntities;
};

export const getActiveCarts = createAsyncThunk<
  any,
  void,
  { extra: ThunkExtraArguments; state: ReduxStore }
>("/checkout/getActiveCarts", async (_, thunkAPI) => {
  try {
    const { userRetailerShoppingCarts, products } =
      await getAllUserRetailerShoppingCarts();
    const sortedCarts = _sortBy(userRetailerShoppingCarts, "retailerId");
    return {
      allCarts: sortedCarts,
      products,
    };
  } catch (error) {
    return Promise.reject(error);
  }
});

export const getMainEntity = createAsyncThunk<
  any,
  { type?: string; id?: string },
  { extra: ThunkExtraArguments; state: ReduxStore }
>(
  "/mainEntity/getMainEntity",
  async ({ type, id }: { type?: string; id?: string }, thunkAPI) => {
    if (type === "brand") {
      const { brandEntity } = thunkAPI.getState();
      if (`${brandEntity?.id}` === `${id}` && brandEntity?.entity) {
        return new Promise((resolve, reject) => {
          resolve({ data: { ...brandEntity?.entity } } as any);
        });
      }
      return getEntityForLandingPage({
        entityId: id as any,
        entityType: type as any,
      });
    }
    if (type === "product") {
      const { userProfile } = thunkAPI.getState();
      const res = await getEntityForLandingPage({
        entityId: id as any,
        entityType: type as any,
      });

      const isAuthenticated = !!userProfile?.userId;
      const productEntity = await fetchProductMetadataWithGraphQL(
        thunkAPI.extra.apolloClient,
        [id as string],
        isAuthenticated
      );

      // the score property is not present in the res.data, so fetch it from the productEntity and overwrite the res.data
      if (productEntity) {
        res.data = {
          ...res.data,
          ...productEntity,
        };
      }
      return res;
    }
    return getEntityForLandingPage({
      entityId: id as any,
      entityType: type as any,
    });
  }
);
